"use client";
import { useTranslation } from "@/app/i18n/client";
import { CaseCategory, CaseStudy } from "@/sanity.types";
import { useEffect, useState } from "react";
import clsx from "clsx";
import CaseTile from "./CaseTile/CaseTile";
import Image from "next/image";
import { urlFor } from "@/sanity/lib/image";
import { AnimatePresence, motion } from "framer-motion";
import dots from "@assets/images/case_dots.svg";
import { useKeenSlider } from "keen-slider/react";

const CaseList = ({ moduleData, lng, cat }: { moduleData: CaseStudy[]; lng: string; cat: string }) => {
	const filtersList = Array.from(
		new Set(
			moduleData.map((caseStudy) => {
				const category = (caseStudy.category as unknown as CaseCategory).slug.current;
				const title = (caseStudy.category as unknown as CaseCategory).title;
				return JSON.stringify({ category, title });
			}),
		),
	).map((item) => JSON.parse(item));

	const orderedFilters = filtersList.sort((a, b) => a.title.localeCompare(b.title));
	const [selectedFilter, setSelectedFilter] = useState<string | null>(cat || "all");
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const [sliderRef] = useKeenSlider<HTMLDivElement>({
		slides: {
			perView: "auto",
			spacing: 12,
		},
	});

	console.log(selectedFilter);

	const { t } = useTranslation(lng);

	useEffect(() => {
		if (cat) {
			setSelectedFilter(cat);
		}
	}, [cat]);

	const handleFilterChange = (filter: string | undefined) => {
		if (filter) {
			setSelectedFilter(filter);
		}
	};

	const filteredDataByFilters = moduleData.filter((caseStudy) => {
		if ((selectedFilter && selectedFilter == (caseStudy.category as unknown as CaseCategory).slug.current) || selectedFilter == "all") {
			return caseStudy;
		}
	});

	return (
		<section className="grid grid-cols-14 gap-y-8 px-grid sm:px-grid-s xs:grid-cols-2 xs:px-grid-xs">
			{/* Filter dropdown */}

			{/* Desktop */}
			<div className="col-span-full flex flex-wrap justify-center gap-[0.3125rem] xs:hidden">
				<div
					className={clsx(
						selectedFilter == "all" ? "border-blue bg-blue text-white" : "text-black hover:border-blue hover:text-blue",
						"z-20 cursor-pointer whitespace-nowrap rounded-[1.875rem] border px-[1.56rem] py-[0.88rem] text-[0.75rem] duration-500 xs:py-2.5",
					)}
					onClick={() => handleFilterChange("all")}
				>
					{t("caseStudies.all")}
				</div>
				{orderedFilters.map((filter) => (
					<div
						key={filter.category}
						className={clsx(
							selectedFilter == filter.category
								? "border-blue bg-blue text-white"
								: "text-black hover:border-blue hover:text-blue",
							"z-20 cursor-pointer whitespace-nowrap rounded-[1.875rem] border px-[1.56rem] py-[0.88rem] text-[0.75rem] duration-500 xs:py-2.5",
						)}
						onClick={() => handleFilterChange(filter.category)}
					>
						{filter.title}
					</div>
				))}
			</div>

			{/* Mobile */}
			<div className="col-span-full mt-8 hidden xs:block" ref={sliderRef}>
				<div className="keen-slider">
					<div
						className={clsx(
							selectedFilter == "all" ? "border-blue bg-blue text-white" : "text-black hover:border-blue hover:text-blue",
							"keen-slider__slide z-20 min-w-fit cursor-pointer whitespace-nowrap rounded-[1.875rem] border px-[1.56rem] py-[0.88rem] text-[0.75rem] transition-colors duration-500 xs:py-2.5",
						)}
						onClick={() => handleFilterChange("all")}
					>
						{t("caseStudies.all")}
					</div>
					{orderedFilters.map((filter) => (
						<div
							key={filter.category}
							className={clsx(
								selectedFilter == filter.category
									? "border-blue bg-blue text-white"
									: "text-black hover:border-blue hover:text-blue",
								"keen-slider__slide z-20 min-w-fit cursor-pointer whitespace-nowrap rounded-[1.875rem] border px-[1.56rem] py-[0.88rem] text-[0.75rem] transition-colors duration-500 xs:py-2.5",
							)}
							onClick={() => handleFilterChange(filter.category)}
						>
							{filter.title}
						</div>
					))}
				</div>
			</div>

			{/* Case studies list */}

			{/* Background image container */}
			<div className="col-span-7 col-start-2 row-start-2 sm:col-span-8 sm:col-start-1 xs:col-span-full">
				<div className="sticky top-40 -z-40 mb-24 grid overflow-hidden rounded-[1.25rem] sm:top-[40vh] xs:top-40">
					{filteredDataByFilters.map((caseStudy, i) => {
						return (
							<div
								key={"image" + i}
								className={clsx(
									hoveredIndex == i ? "opacity-100" : "opacity-0",
									"relative col-start-1 row-start-1 aspect-[3/2] w-full duration-500 xs:aspect-[22/29]",
								)}
							>
								<Image
									src={caseStudy.thumbnail ? urlFor(caseStudy.thumbnail).url() : ""}
									fill
									style={{ objectFit: "cover" }}
									alt={caseStudy.thumbnail?.alt ? caseStudy.thumbnail.alt : ""}
									sizes="(max-width: 600px) 90vw, 50vw"
								/>
							</div>
						);
					})}
					<Image
						src={dots.src}
						height={dots.height}
						width={dots.width}
						alt="Background dots"
						className="absolute -z-20 -translate-x-1/2"
					/>
				</div>
			</div>

			{/* Case studies cards list */}
			<div className="col-span-6 col-start-8 row-start-2 mt-24 flex flex-col gap-y-[0.62rem] pb-48 sm:col-span-8 sm:col-start-7 sm:gap-y-5 xs:col-span-full xs:col-start-1">
				<AnimatePresence mode="popLayout">
					{filteredDataByFilters.map((caseStudy, i) => {
						return (
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.5 }}
								className="relative z-30 xs:px-10"
								key={"case-tile" + i}
								onMouseEnter={() => {
									setHoveredIndex(i);
								}}
							>
								<CaseTile
									tileData={caseStudy}
									isActive={hoveredIndex == i}
									index={i}
									lng={lng}
									setHoveredIndex={setHoveredIndex}
								/>
							</motion.div>
						);
					})}
				</AnimatePresence>
				{filteredDataByFilters.length == 0 && <p className="text-h4 xs:text-h4-xs">{t("caseStudies.noResults")}</p>}
			</div>
		</section>
	);
};

export default CaseList;
